// Arquivo: src/components/charts.tsx  (colar o conteúdo INTEGRAL no Code Mode)
// Fábricas de gráficos SVG do Dash Engenharia (porta fiel do app local, sem
// bibliotecas externas). Paleta: navy #0F172A, verde #10B981, vermelho #EF4444,
// azul #2563EB, cinza #94A3B8, amarelo #F59E0B; gridlines #F1F5F9.

import type { ReactNode } from 'react'
import { moneyCompact, numFormat } from '@/lib/format'

export const CHART_COLORS = {
  navy: '#0F172A', green: '#10B981', red: '#EF4444', blue: '#2563EB',
  gray: '#94A3B8', yellow: '#F59E0B',
}
export const DONUT_PALETTE = ['#2563EB', '#10B981', '#F59E0B', '#6366F1', '#EC4899']

export function ChartPanel({ title, subtitle, children, className = '' }: { title: string; subtitle?: string; children: ReactNode; className?: string }) {
  return (
    <div className={`panel ${className}`}>
      <div className="panel-head">
        <div className="panel-title">{title}</div>
        {subtitle ? <div className="panel-sub">{subtitle}</div> : null}
      </div>
      {children}
    </div>
  )
}

export function ChartEmpty({ message }: { message: string }) {
  return <div className="chart-empty">{message}</div>
}

function niceCeil(value: number): number {
  if (value <= 0) return 1
  const exp = Math.floor(Math.log10(value))
  const base = Math.pow(10, exp)
  const frac = value / base
  const nice = frac <= 1 ? 1 : frac <= 1.2 ? 1.2 : frac <= 1.5 ? 1.5 : frac <= 2 ? 2
    : frac <= 2.5 ? 2.5 : frac <= 3 ? 3 : frac <= 4 ? 4 : frac <= 5 ? 5 : frac <= 6 ? 6 : frac <= 8 ? 8 : 10
  return nice * base
}

function Legend({ items }: { items: Array<{ name: string; color: string }> }) {
  return (
    <div className="chart-legend">
      {items.map(item => (
        <span key={item.name}><span className="legend-swatch" style={{ background: item.color }} />{item.name}</span>
      ))}
    </div>
  )
}

export function HBarList({ items, color = CHART_COLORS.green, format = numFormat, emptyMessage = 'Sem dados para exibir neste filtro.' }:
{ items: Array<{ label: string; value: number; title?: string }>; color?: string; format?: (v: number) => string; emptyMessage?: string }) {
  const visible = items.filter(item => Number.isFinite(item.value))
  if (!visible.length) return <ChartEmpty message={emptyMessage} />
  const max = Math.max(...visible.map(item => item.value), 0.0001)
  return (
    <div>
      {visible.map(item => (
        <div className="hbar-row" key={item.label} title={item.title ?? item.label}>
          <div className="hbar-label" title={item.label}>{item.label}</div>
          <div className="hbar-track">
            <div className="hbar-fill" style={{ width: `${Math.min(100, (item.value / max) * 100)}%`, background: color }} />
          </div>
          <div className="hbar-value">{format(item.value)}</div>
        </div>
      ))}
    </div>
  )
}

export function ColumnChart({ categories, series, height = 225, valueFormat = moneyCompact, emptyMessage = 'Sem dados para exibir neste filtro.' }:
{
  categories: string[]
  series: Array<{ name: string; color: string; values: number[] }>
  height?: number
  valueFormat?: (v: number) => string
  emptyMessage?: string
}) {
  const flat = series.flatMap(s => s.values).filter(v => Number.isFinite(v) && v !== 0)
  if (!categories.length || !flat.length) return <ChartEmpty message={emptyMessage} />
  const W = Math.max(340, categories.length * (series.length * 30 + 26) + 90)
  const m = { top: 12, right: 14, bottom: 58, left: 66 }
  const plotW = W - m.left - m.right
  const plotH = height - m.top - m.bottom
  const max = niceCeil(Math.max(...flat, 0))
  const slot = plotW / categories.length
  const barW = Math.min(26, slot / (series.length + 0.4))
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => max * f)
  const short = (text: string) => (text.length > 20 ? `${text.slice(0, 20)}…` : text)
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} role="img">
        {ticks.map((tick, i) => {
          const y = m.top + plotH - (tick / max) * plotH
          return (
            <g key={i}>
              <line x1={m.left} x2={W - m.right} y1={y} y2={y} stroke="#F1F5F9" strokeWidth={1} />
              <text x={m.left - 8} y={y + 3} textAnchor="end" fontSize={10} fill="#64748B">{valueFormat(tick)}</text>
            </g>
          )
        })}
        {categories.map((category, ci) => {
          const cx = m.left + slot * ci + slot / 2
          return (
            <g key={category}>
              {series.map((s, si) => {
                const value = s.values[ci] ?? 0
                const h = max > 0 ? (Math.max(value, 0) / max) * plotH : 0
                const x = cx - (series.length * barW) / 2 + si * barW
                return (
                  <rect key={s.name} x={x} y={m.top + plotH - h} width={barW - 2} height={Math.max(h, 0)} rx={3} fill={s.color}>
                    <title>{`${category}\n${s.name}: ${valueFormat(value)}`}</title>
                  </rect>
                )
              })}
              <text transform={`rotate(-32 ${cx} ${m.top + plotH + 14})`} x={cx} y={m.top + plotH + 14}
                textAnchor="end" fontSize={10} fill="#475569">{short(category)}</text>
            </g>
          )
        })}
        <line x1={m.left} x2={W - m.right} y1={m.top + plotH} y2={m.top + plotH} stroke="#CBD5E1" />
      </svg>
      <Legend items={series.map(s => ({ name: s.name, color: s.color }))} />
    </div>
  )
}

export function LineChart({ labels, values, color = CHART_COLORS.blue, height = 280, valueFormat = moneyCompact, emptyMessage = 'Sem dados para exibir.' }:
{ labels: string[]; values: number[]; color?: string; height?: number; valueFormat?: (v: number) => string; emptyMessage?: string }) {
  const points = values.map((v, i) => ({ v, label: labels[i] })).filter(p => Number.isFinite(p.v))
  if (points.length < 2) return <ChartEmpty message={emptyMessage} />
  const W = 760
  const m = { top: 14, right: 16, bottom: 46, left: 66 }
  const plotW = W - m.left - m.right
  const plotH = height - m.top - m.bottom
  const max = niceCeil(Math.max(...points.map(p => p.v), 0.0001))
  const x = (i: number) => m.left + (plotW * i) / (points.length - 1)
  const y = (v: number) => m.top + plotH - (v / max) * plotH
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.v)}`).join(' ')
  const areaPath = `${linePath} L${x(points.length - 1)},${m.top + plotH} L${x(0)},${m.top + plotH} Z`
  const skip = Math.max(1, Math.ceil(points.length / 12))
  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} role="img">
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const ty = m.top + plotH - f * plotH
        return (
          <g key={i}>
            <line x1={m.left} x2={W - m.right} y1={ty} y2={ty} stroke="#F1F5F9" />
            <text x={m.left - 8} y={ty + 3} textAnchor="end" fontSize={10} fill="#64748B">{valueFormat(max * f)}</text>
          </g>
        )
      })}
      <path d={areaPath} fill={color} opacity={0.06} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={2.4} />
      {points.map((p, i) => (
        <g key={p.label}>
          <circle cx={x(i)} cy={y(p.v)} r={3.5} fill="#fff" stroke={color} strokeWidth={2.2}>
            <title>{`${p.label}\n${valueFormat(p.v)}`}</title>
          </circle>
          {(i % skip === 0 || i === points.length - 1) && (
            <text transform={`rotate(-32 ${x(i)} ${m.top + plotH + 16})`} x={x(i)} y={m.top + plotH + 16} textAnchor="end" fontSize={10} fill="#475569">{p.label}</text>
          )}
        </g>
      ))}
    </svg>
  )
}

export function DonutChart({ items, palette = DONUT_PALETTE, size = 125, valueFormat = moneyCompact, emptyMessage = 'Sem dados para exibir.' }:
{ items: Array<{ label: string; value: number }>; palette?: string[]; size?: number; valueFormat?: (v: number) => string; emptyMessage?: string }) {
  const visible = items.filter(item => item.value > 0).slice(0, 5)
  if (!visible.length) return <ChartEmpty message={emptyMessage} />
  const total = visible.reduce((sum, item) => sum + item.value, 0)
  const r = size / 2 - 9
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        {visible.map((item, i) => {
          const frac = item.value / total
          const dash = frac * c
          const circle = (
            <circle key={item.label} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={palette[i % palette.length]}
              strokeWidth={18} strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}>
              <title>{`${item.label}: ${valueFormat(item.value)} (${(frac * 100).toFixed(1)}%)`}</title>
            </circle>
          )
          offset += dash
          return circle
        })}
      </svg>
      <div>
        {visible.map((item, i) => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '4px 0', fontSize: 11.5, color: '#475569' }}>
            <span className="legend-swatch" style={{ background: palette[i % palette.length], borderRadius: '9999px', width: 7, height: 7 }} />
            <span style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.label}>{item.label}</span>
            <span className="mono" style={{ color: '#64748B' }}>{valueFormat(item.value)}</span>
            <span className="mono muted">{((item.value / total) * 100).toFixed(1).replace('.', ',')}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function ComboChart({ labels, barName, barColor = CHART_COLORS.navy, barValues, lineName, lineColor = CHART_COLORS.green, lineValues, height = 230, emptyMessage = 'Sem dados para exibir.' }:
{
  labels: string[]
  barName: string
  barColor?: string
  barValues: number[]
  lineName: string
  lineColor?: string
  lineValues: number[]
  height?: number
  emptyMessage?: string
}) {
  const barMax = Math.max(...barValues.map(v => Math.max(v, 0)), 0.0001)
  const anyBar = barValues.some(v => v > 0)
  const anyLine = lineValues.some(v => v > 0)
  if (!anyBar && !anyLine) return <ChartEmpty message={emptyMessage} />
  const W = 760
  const m = { top: 14, right: 60, bottom: 46, left: 66 }
  const plotW = W - m.left - m.right
  const plotH = height - m.top - m.bottom
  const barTop = niceCeil(barMax)
  const slot = plotW / Math.max(labels.length, 1)
  const bw = Math.min(30, slot * 0.55)
  const lineMax = Math.max(...lineValues, 0.0001)
  const x = (i: number) => m.left + slot * i + slot / 2
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} role="img">
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const ty = m.top + plotH - f * plotH
          return (
            <g key={i}>
              <line x1={m.left} x2={W - m.right} y1={ty} y2={ty} stroke="#F1F5F9" />
              <text x={m.left - 8} y={ty + 3} textAnchor="end" fontSize={10} fill="#64748B">{moneyCompact(barTop * f)}</text>
              <text x={W - m.right + 8} y={ty + 3} fontSize={10} fill="#64748B">{(f * 100).toFixed(0)}%</text>
            </g>
          )
        })}
        {labels.map((label, i) => {
          const h = (Math.max(barValues[i] ?? 0, 0) / barTop) * plotH
          const lx = x(i)
          const ly = m.top + plotH - (Math.min(Math.max(lineValues[i] ?? 0, 0), lineMax) / lineMax) * plotH
          return (
            <g key={label}>
              <rect x={lx - bw / 2} y={m.top + plotH - h} width={bw} height={Math.max(h, 0)} rx={3} fill={barColor}>
                <title>{`${label}\n${barName}: ${moneyCompact(barValues[i] ?? 0)}`}</title>
              </rect>
              <circle cx={lx} cy={ly} r={3.2} fill="#fff" stroke={lineColor} strokeWidth={2}>
                <title>{`${label}\n${lineName}: ${(lineValues[i] ?? 0).toFixed(2).replace('.', ',')}%`}</title>
              </circle>
              {(i % Math.max(1, Math.ceil(labels.length / 12)) === 0 || i === labels.length - 1) && (
                <text transform={`rotate(-32 ${lx} ${m.top + plotH + 14})`} x={lx} y={m.top + plotH + 14} textAnchor="end" fontSize={10} fill="#475569">{label}</text>
              )}
            </g>
          )
        })}
      </svg>
      <Legend items={[{ name: barName, color: barColor }, { name: lineName, color: lineColor }]} />
    </div>
  )
}
